import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, shareReplay, tap } from 'rxjs';
import { CommonService } from 'src/@services/common.service';
import { ApiEnvelope, PolicyType, PublicPolicy } from './account-api.models';
import { EndPoints, ResponseStatus } from 'src/@shared/shared/enum';
import { environment } from 'src/environments/environment';

/** `User/GetPaybevUserDetails` — only the fields the profile screens use. */
export interface PaybevUserDetails {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  title: string | null;
  location: string | null;
  linkedInUrl: string | null;
  profile: string | null;
  agency: { id: string; name: string }[] | null;
  role?: string | null;
  /** Last save on User Details - the form, a verified phone change or a picture. Null until the first one. */
  profileUpdatedOn: string | null;
  /** Null until the user next sets a password (the API has no earlier history). */
  passwordChangedOn: string | null;
  /** When the account was created; stands in for the password date until it is first changed. */
  createdOn?: string | null;
  /** False for a user who has never set a password (e.g. signs in with a passkey only). */
  hasPassword?: boolean;
}

/** `User/ProfileUpdate` body. Names are read-only in the UI but the API expects them on every save. */
export interface ProfileUpdateRequest {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  title: string | null;
  location: string | null;
  linkedInUrl: string | null;
}

/** `User/GetUserAgencies` — every agency the user can access (all of them for a super admin), by name. */
export interface UserAgency {
  id: string;
  name: string;
  profile: string | null;
}

/** `User/GetUserAccountsSites` — the signed-in user's sites grouped by account. */
export interface UserAccountSites {
  accountId: string;
  accountName: string;
  accountProfile?: string | null;
  sites: { siteId: string; siteName: string }[];
}

/** `TermsAcceptance/MyAgreements` card. The one with `isPolicy` false is the caller's own agreement. */
export interface MyAgreementCard {
  termsType: number;
  title: string;
  versionLabel: string | null;
  signedOn: string | null;
  isPolicy: boolean;
}

/** `TermsAcceptance/MyAgreement/{termsType}` — the version the caller signed, plus the signing evidence. */
export interface UserAgreement {
  termsType: number;
  title: string;
  versionLabel: string;
  /**
   * The version on their acceptance record. Differs from `versionLabel` only when a minor was
   * activated after they signed and carried forward to them - what the "Terms & Conditions
   * Updated" notice reports. Null for anyone who has never accepted.
   */
  acceptedVersionLabel: string | null;
  contentHtml: string;
  acceptedOn: string | null;
  uniqueId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class UserProfileApiService {
  /** Per-sign-in response cache for the read-only profile calls, keyed by endpoint. */
  private readonly cache = new Map<string, { token: string | null; request: Observable<unknown> }>();

  constructor(private _cS: CommonService, private _http: HttpClient) {}

  /**
   * Shared by the header avatar (ProfilePictureService) and the profile screens, so a page load
   * makes one `GetPaybevUserDetails` call. A profile or picture change drops it.
   */
  getDetails(): Observable<ApiEnvelope<PaybevUserDetails>> {
    return this.cachedGet<PaybevUserDetails>(EndPoints.GetPaybevUserDetails);
  }

  clearDetailsCache(): void {
    this.cache.delete(EndPoints.GetPaybevUserDetails);
  }

  updateProfile(body: ProfileUpdateRequest): Observable<ApiEnvelope<ProfileUpdateRequest>> {
    return this._cS.API_POST(EndPoints.UpdateProfileData, body, false).pipe(tap(() => this.clearDetailsCache()));
  }

  /** Access tab data. The API call is slow (~1.5s), so it is fetched once per sign-in, not per visit. */
  getAccountsSites(): Observable<ApiEnvelope<UserAccountSites[]>> {
    return this.cachedGet<UserAccountSites[]>(EndPoints.GetUserAccountsSites);
  }

  /** Access tab agencies; cached per sign-in like the accounts list. */
  getAgencies(): Observable<ApiEnvelope<UserAgency[]>> {
    return this.cachedGet<UserAgency[]>(EndPoints.GetUserAgencies);
  }

  getMyAgreements(): Observable<ApiEnvelope<MyAgreementCard[]>> {
    return this._cS.API_GET(EndPoints.MyAgreements, false);
  }

  getMyAgreement(termsType: number): Observable<ApiEnvelope<UserAgreement>> {
    return this._cS.API_GET(`${EndPoints.MyAgreement}/${termsType}`, false);
  }

  /** Published Privacy / Cookie policy shown beside the signed agreement. */
  getPolicy(type: PolicyType): Observable<ApiEnvelope<PublicPolicy>> {
    return this._cS.API_GET(`${EndPoints.TermsPolicy}/${type}`, false);
  }

  /**
   * The rendered PDF. Not CommonService.API_GET: that only maps JSON, so the auth headers are
   * attached here and the body is read as a Blob. View and Download share these same bytes.
   */
  getAgreementPdf(termsType: number, isPolicy: boolean): Observable<Blob> {
    const base = isPolicy ? EndPoints.TermsPolicy : EndPoints.MyAgreement;
    return this._http.get(`${environment.apiURL}${base}/${termsType}/Pdf`, {
      headers: this._cS.GetHeader(),
      responseType: 'blob',
    });
  }

  /**
   * One shared request per endpoint for the current sign-in token: callers during the first call
   * share it, later callers get the stored response. A new token refetches; a failure is not kept.
   */
  private cachedGet<T>(url: string): Observable<ApiEnvelope<T>> {
    const token = this._cS.getTokenObj();
    const hit = this.cache.get(url);
    if (hit && hit.token === token) {
      return hit.request as Observable<ApiEnvelope<T>>;
    }
    const drop = (): void => {
      if (this.cache.get(url)?.request === request) {
        this.cache.delete(url);
      }
    };
    const request: Observable<ApiEnvelope<T>> = (this._cS.API_GET(url, false) as Observable<ApiEnvelope<T>>).pipe(
      tap({ next: (response) => response.responseStatus != ResponseStatus.success && drop(), error: drop }),
      shareReplay(1)
    );
    this.cache.set(url, { token, request });
    return request;
  }
}
